import { gql } from '@apollo/client'
import { useConfirmDialog, useError, useMutation, useRole, useToast } from '@ztechapps/common.hook'
import { invoicePermissions } from '@ztechapps/totalmilk-payme.common'

const query = gql`
  mutation finalizeInvoices($input: JSON!) {
    finalizeInvoices(input: $input) {
      nModified
    }
  }
`

export const useFinalize = (): [
  onFinalize: (invoiceIds: string[]) => Promise<void>,
  {
    error: any
    loading: boolean
    hasPermission: boolean
  },
] => {
  const [finalizeInvoices, , { loading }] = useMutation({
    query,
    options: {
      mutationOptions: { refetchQueries: ['allInvoice'] },
    },
  })
  const { onError, error } = useError()
  const { hasRole: canEdit } = useRole({ roleName: invoicePermissions.canEdit.value })

  const toaster = useToast()

  const { confirm } = useConfirmDialog({
    text: 'This action will send the selected invoices to the customers, and copy it to quickbooks. Once the invoice was sent, you cannot edit it anymore. Do you wish to continue?',
    title: `Finalize Invoice`,
    buttons: { confirm: { text: `Yes, finalize it!` } },
  })

  const hasPermission = canEdit

  const onSubmit = async (invoiceIds: string[] = []) => {
    try {
      if (!hasPermission) {
        throw new Error('You do not have permission to finalize invoices.')
      }

      const confirmedAction = async () => {
        const { data } = await finalizeInvoices({ input: { invoiceIds, _id: 'any' } })

        const nModified = data?.finalizeInvoices?.nModified
        if (!nModified) throw new Error('Unknown error.')
        toaster.showSuccess({ message: `Successfully finalized ${nModified} invoices.` })
      }

      await confirm(confirmedAction)
    } catch (e) {
      onError(e)
    }
  }

  return [onSubmit, { error, loading, hasPermission }]
}
